import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Card, CardContent, CardHeader, TextField, Typography, Snackbar, Alert } from '@mui/material';
import { WEBSOCKET_URL } from '../../utils/constants';

interface LogLine {
  style: string;
  text: string;
  action: string;
}

export const CodeBaseIndex: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [codingLocation, setCodingLocation] = useState('');
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const logBoxRef = useRef<HTMLDivElement>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  useEffect(() => {
    const newSocket = new WebSocket(WEBSOCKET_URL);

    newSocket.onopen = () => {
      console.log('WebSocket connected');
      setSocket(newSocket);
    };

    newSocket.onclose = () => {
      console.log('WebSocket disconnected');
      setSocket(null);
    };

    newSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    newSocket.onmessage = (event) => {
      const parsedMessage = JSON.parse(event.data);
      console.log('Received message from WebSocket:', parsedMessage);
      if (parsedMessage.action) {
        setLogLines((prevLogLines) => [
            ...prevLogLines,
            {
            style: parsedMessage && parsedMessage.status ? 
                    (parsedMessage.status.toLowerCase().includes('success') ? 'success' :
                    parsedMessage.status.toLowerCase().includes('error') ? 'error' :
                    parsedMessage.status.toLowerCase().includes('failed') ? 'error' :
                    parsedMessage.status.toLowerCase().includes('retry') ? 'notification' :
                    'general') : 'general',
            text: `${parsedMessage.action} - ${parsedMessage.status} - ${parsedMessage.message}`,
            action: parsedMessage.action
            },
        ]);
      } else if (String(parsedMessage.message).includes('Internal server error')) {
        setLogLines((prevLogLines) => [
            ...prevLogLines,
            {
            style: 'error',
            text: `${parsedMessage.message}`,
            action: ''
            },
        ]);
      }
    };

    return () => {
      newSocket.close();
    };
  }, []);

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [logLines]);

  const handleSendQuestion = () => {
    if (!question.trim() || !codingLocation.trim()) {
      setSnackbarMessage(
        !question.trim() && !codingLocation.trim()
          ? "Please enter both a question and a coding location."
          : !question.trim()
          ? "Please enter a question."
          : "Please enter a codebase location."
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }
    if (socket && socket.readyState === WebSocket.OPEN) {
      const payload = JSON.stringify({
        action: 'CODEBASE_INDEX_QUERY',
        question,
        coding_location: codingLocation
      });
      setLogLines([]);
      socket.send(payload);
      console.log('Sent message to WebSocket:', question);
    } else {
      console.error('WebSocket is not connected');
    }
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Box sx={{ padding: '20px', paddingTop: '40px', paddingBottom: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
      <Typography variant="h3" sx={{ marginBottom: '40px', color: '#333', textAlign: 'center' }}>Codebase Index Query</Typography>
      <TextField
        label="Enter codebase location"
        variant="outlined"
        value={codingLocation}
        name="coding_location"
        onChange={(e) => setCodingLocation(e.target.value)}
        sx={{ marginBottom: '20px', width: '100%' }}
      />
      <Box 
        id="logBox" 
        ref={logBoxRef}
        sx={{ 
          padding: '10px', 
          backgroundColor: '#1e1e1e', 
          minHeight: '300px', 
          maxHeight: 'calc(100vh - 362px)', 
          borderRadius: '8px', 
          overflowY: 'scroll' 
        }}
      >
        {logLines.slice().map((log, index) => (
          <Typography key={index} sx={{ 
            whiteSpace: 'pre-wrap', 
            marginLeft: '1rem', 
            marginTop: '.25rem', 
            textAlign: 'left', 
            fontFamily: 'Source Code Pro, monospace', 
            fontSize: '15px',
            color: log.style === 'info' ? '#6699CC' :
                   log.style === 'error' ? '#EC5f67' :
                   log.style === 'notification' ? '#FAC863' :
                   log.style === 'success' ? '#98fa63' :
                   log.style === 'success-partial' ? '#ffb700' :
                   log.style === 'fail' ? '#ff000d' :
                   log.action === 'QA_ENGINEER' ? '#E6EE9C' :
                   log.action === 'SERVERLESS_DEVELOPER' ? '#B3E5FC' :
                   log.action === 'SOLUTIONS_ARCHITECT' ? '#FFDAB9' :
                   log.action === 'BUSINESS_ANALYST' ? '#C8E6C9' :
                   log.style === 'general' ? '#fff' :
                   '#d4d4d4'
          }}>
            <div dangerouslySetInnerHTML={{ __html: log.text.replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', paddingTop: '20px', marginBottom: '10px', flexDirection: 'row', width: '100%' }}>
        <TextField
          label="Enter your codebase index query"
          variant="outlined"
          value={question}
          name="bss_coding_question"
          onChange={(e) => setQuestion(e.target.value)}
          sx={{ marginBottom: '10px', flexGrow: 1, marginRight: '10px' }}
          multiline
          rows={1}
        />
        <Button variant="contained" color="primary" disabled={!socket || socket.readyState !== WebSocket.OPEN} onClick={handleSendQuestion} sx={{ marginBottom: '10px', height: '56px' }}>
          Send Query
        </Button>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CodeBaseIndex;
